import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { UseQueryOptions, useQuery } from "react-query";
import {
  AmountAndCurrency,
  Contact,
  Employer,
  ServiceSector,
  TransactionReason,
  TransactionStatus,
  TransactionType,
  Plan
} from "../models";
import { fetchData } from "./fetchData";
import { useConfig } from "./useConfig";

export type Pagination<T> = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  links: {
    next: string;
    previous: string;
  };
  items: T[];
};

export type Account = {
  id: string;
  name: string;
  description: string;
  currency: string;
  startingBalance: AmountAndCurrency;
  availableBalance: AmountAndCurrency;
  actualBalance: AmountAndCurrency;
  createdAt: Date;
  planId: string;
  tierId: string;
  renewalOptions: {
    startDate: Date;
    periodInMonths: number;
  };
};

export type Feature = {
  name: string;
  isEnabled: boolean;
};

export type Card = {
  id: string;
  name: string;
  description: string;
  maskedCardNumber: string;
  expiry: string;
  cardToken: string;
  status: CardStatus;
};

export enum CardStatus {
  Created = "CREATED",
  Active = "ACTIVE",
  Inactive = "INACTIVE",
  Removed = "REMOVED",
  None = "NONE"
}

export type Transaction = {
  id: string;
  accountId: string;
  cardId: string;
  type: TransactionType; // 'purchase' | 'refund' etc
  transactionAmount: AmountAndCurrency;
  billingAmount: AmountAndCurrency;
  status: TransactionStatus;
  timestamp: string;
  cardAcceptor: string;
  employeeId: string;
  mccCodeRaw: string;
  reason: TransactionReason;
};

export type Employee = {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  imageUrl: string;
  status: EmployeeStatus;
  contact: Contact;
  accounts: Account[];
  tags: Tag[];
  cards: { id: string; status: CardStatus }[];
  stats?: EmployeeStats;
};

export enum EmployeeStatus {
  "Active" = "ACTIVE",
  "Created" = "CREATED",
  "Inactive" = "INACTIVE"
  // "Deleted" = "DELETED",
}

export type EmployeeStats = {
  invitationSent: string; // datetime
  cardIssued: string; // datetime
  cardActivated: string; // datetime
  kyc?: {
    status: "STARTED" | "DECLINED" | "APPROVED";
    lastUpdatedTime: string;
    inquiryId: string;
  };
};

export type UserInfo = {
  employeeId: string;
  user?: Employee;
};

export type TagCategory = {
  id: string;
  name: string;
  description: string;
  tags: Tag[];
};

export type Tag = {
  id: string;
  name: string;
  tagCategoryId: string;
  description?: string;
  color?: string;
};

export type TagReference = {
  tagCategoryId: string;
  tagId?: string;
};

export type MessageTemplate = {
  id: string;
  name: string;
  description: string;
  templateId: string;
  type: string;
  parameters: string[];
};

interface QueryOptions<TData> {
  endpoint: string;
  queryString?: string;
  pagination?: {
    page: number;
    pageSize: number;
  };
  queryKey?: string;
  options?: UseQueryOptions<TData>;
}

function useCustomQuery<TData>(options: QueryOptions<TData>) {
  const {
    endpoint,
    queryString,
    pagination,
    queryKey,
    options: customOptions
  } = options;
  const { getToken } = useKindeAuth();
  const { config } = useConfig();

  const query = useQuery<TData>(
    queryKey || endpoint,
    () =>
      fetchData(
        `${config?.API_URL}${endpoint}`,
        getToken,
        queryString,
        pagination
      ),
    {
      ...customOptions,
      enabled: customOptions?.enabled !== false && !!config,
      keepPreviousData: true
    }
  );

  return query;
}

export function useData() {
  const { getToken, getClaim } = useKindeAuth();
  const { config } = useConfig();

  const employerId = getClaim("external_org_id")?.value;

  const { data: userInfoData } = useQuery<UserInfo>(
    "/user-info",
    () => fetchData(`${config?.API_URL}/user-info`, getToken),
    {
      enabled: !!config
    }
  );

  const employer = useCustomQuery<Employer>({
    endpoint: `/employers/${employerId}`,
    options: {
      enabled: !!userInfoData
    }
  });

  const employees = useCustomQuery<Pagination<Employee>>({
    endpoint: `/employers/${employerId}/employees`,
    pagination: {
      page: 0,
      pageSize: 10000
    },
    options: {
      enabled: !!userInfoData
    }
  });

  const plans = useCustomQuery<Pagination<Plan>>({
    endpoint: `/employers/${employerId}/plans`,
    pagination: {
      page: 0,
      pageSize: 1000
    },
    options: {
      enabled: !!userInfoData
    }
  });

  const serviceSectors = useCustomQuery<Pagination<ServiceSector>>({
    endpoint: `/service-sectors`,
    options: {
      enabled: !!userInfoData
    }
  });

  const tagCategories = useCustomQuery<Pagination<TagCategory>>({
    endpoint: `/employers/${employerId}/tag-categories`,
    options: {
      enabled: !!userInfoData
    }
  });

  const messageTemplates = useCustomQuery<MessageTemplate[]>({
    endpoint: `/message-templates`
  });

  const transactions = useCustomQuery<Pagination<Transaction>>({
    endpoint: `/transactions`,
    queryString: `employerid=${employerId}`,
    pagination: {
      page: 0,
      pageSize: 10000
    },
    options: {
      enabled: !!userInfoData
    }
  });

  const features = useCustomQuery<Feature[]>({
    endpoint: `/features`
  });

  return {
    employerId,
    employer,
    employees,
    plans,
    serviceSectors,
    userInfoData: {
      ...userInfoData,
      user: employees.data?.items.find(
        (employee) => employee.id === userInfoData?.employeeId
      )
    } as UserInfo | undefined,
    tagCategories,
    messageTemplates,
    transactions,
    features
  };
}
